<template>
  <WdsPopover
    trigger-element-id="candidate-stage-popover"
    placement="top"
    :fallback-placements="['top-start', 'bottom', 'bottom-end', 'left']"
    auto-update
    z-index="20"
    class="tw-p-6"
    global
  >
    <template #trigger="{ togglePopover }">
      <span class="tw-cursor-pointer" @click.stop="togglePopover">
        <slot />
      </span>
    </template>
    <div class="tw-flex tw-flex-col tw-gap-lg tw-min-w-[280px]" data-test="candidate-stage-history">
      <WdsProgress
        v-if="progressSteps.length > 0"
        class="expanded"
        secondary
        :steps="progressSteps"
        :current="[]"
        :most-completed-step="mostCompletedProgressStep"
        :failed-steps="[]"
        sm
      >
        <template #parent="props">
          <div class="tw-min-w-full tw-max-w-[400px] tw-ml-4">
            <div class="tw-flex tw-flex-col tw-gap-1">
              <div class="tw-flex tw-flex-row tw-justify-between">
                <div>
                  <WdsText
                    large
                    primary
                    :text="props.mStep.name"
                    :tertiary="!props.mStep.completed"
                    :data-test="'stage-history-step-' + props.mIndex + '-name'"
                  />
                </div>
                <div class="tw-mr-8">
                  <WdsText
                    v-if="props.mStep.date"
                    secondary
                    :text="props.mStep.date"
                    :tertiary="!props.mStep.completed"
                  />
                </div>
              </div>
              <div v-if="props.mStep.description" class="tw-w-3/4">
                <WdsText
                  default
                  secondary
                  :text="props.mStep.description"
                  :tertiary="!props.mStep.completed"
                />
              </div>
            </div>
          </div>
        </template>
      </WdsProgress>
      <WdsText v-else secondary>
        {{ $t('No stage history available.') }}
      </WdsText>
    </div>
  </WdsPopover>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WdsPopover, WdsProgress, WdsText } from '@worksome/wds-components'
import moment from 'moment'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { JobCandidateStepList } from '@/components/Job/constants'
import type { JobCandidateHiringStep } from '@/gql/graphql'

interface StageHistoryEntry {
  stage: JobCandidateHiringStep
  changedByName?: string | null
  createdAt: string
}

const props = defineProps<{
  stageHistory: StageHistoryEntry[]
}>()

const progressSteps = computed(() =>
  props.stageHistory.map((entry) => ({
    name: JobCandidateStepList[entry.stage]?.label ?? entry.stage,
    url: '',
    description: entry.changedByName ? `by ${entry.changedByName}` : '',
    date: moment(entry.createdAt).format('MMM D, YYYY'),
    completed: true,
  })),
)

const mostCompletedProgressStep = computed(() => {
  const lastIndex = progressSteps.value.length - 1
  return lastIndex >= 0 ? [lastIndex] : []
})
</script>

<style lang="scss" scoped>
:deep(.wds-main-step) {
  align-items: start !important;
}

:deep(.wds-progress) {
  .wds-step:last-child .wds-main-step:before {
    top: calc(-100% + 10px);
  }
}

:deep(.completed-not-active) {
  cursor: auto !important;
}

:deep(.wds-chevron-container) {
  display: none !important;
}
</style>
